/**
 * NotesPanel — shift notes / special details
 *
 * Two modes:
 *   • View mode (default): renders existing notes as a bulleted list.
 *     This is the only mode officers ever see — read-only, no edit button.
 *   • Edit mode (supervisors only): shows a textarea for editing. Supervisors
 *     enter this by tapping the "Edit" button and exit via "Save" or "Cancel".
 *
 * Notes are stored as `string[]` in `fom_notes` and bidirectionally synced
 * with `fom_daily_posting.notes` (see desktop store + postingBridge).
 */
import { useState, useEffect } from 'react'
import { useDesktopStore } from '../../stores/desktop'
import { useAuthStore, isExecRole } from '../../stores/auth'
import { markDirty } from '../../stores/sync'

export default function NotesPanel() {
  const { notes, setNotes } = useDesktopStore()
  const session = useAuthStore(s => s.session)
  const canEdit = isExecRole(session?.rank ?? '')

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  // Whenever notes change externally (cloud pull, posting edit) and we're not
  // currently editing, sync the draft so the next "Edit" tap starts fresh.
  useEffect(() => {
    if (!editing) setDraft(notes.join('\n'))
  }, [notes, editing])

  function startEdit() {
    setDraft(notes.join('\n'))
    setEditing(true)
  }

  function cancelEdit() {
    setDraft(notes.join('\n'))
    setEditing(false)
  }

  function saveEdit() {
    markDirty()
    setNotes(draft.split('\n').map(l => l.trim()).filter(Boolean))
    setEditing(false)
  }

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: 14,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
          paddingBottom: 8,
          borderBottom: '1px solid var(--border)',
          gap: 8,
        }}
      >
        <span
          style={{
            color: '#c9a84c',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.8,
          }}
        >
          Notes / Special Details
        </span>
        {canEdit && !editing && (
          <button
            onClick={startEdit}
            title="Edit notes"
            style={{
              background: 'rgba(201,168,76,0.12)',
              border: '1px solid rgba(201,168,76,0.4)',
              color: '#c9a84c',
              borderRadius: 6,
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ✎ Edit
          </button>
        )}
        {canEdit && editing && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={cancelEdit}
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                color: 'var(--text-muted)',
                borderRadius: 6,
                padding: '4px 12px',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={saveEdit}
              style={{
                background: 'linear-gradient(135deg,#c9a84c,#dbb960)',
                border: 'none',
                color: '#1a2744',
                borderRadius: 6,
                padding: '4px 14px',
                fontSize: 11,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              ✓ Save
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Enter shift notes, special details, or announcements..."
          autoFocus
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: 'var(--bg-deep)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 10,
            color: 'var(--text-primary)',
            fontSize: 12,
            lineHeight: 1.7,
            minHeight: 120,
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'inherit',
          }}
        />
      ) : notes.length === 0 ? (
        <div
          style={{
            padding: '14px 4px',
            color: 'var(--text-muted)',
            fontSize: 12,
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          No notes for this shift.
        </div>
      ) : (
        <div
          style={{
            background: 'var(--bg-deep)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
            }}
          >
            {notes.map((n, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 14px',
                  background: i % 2 === 0 ? 'var(--bg-surface)' : 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: 12,
                  lineHeight: 1.55,
                }}
              >
                <span
                  style={{
                    color: 'var(--accent)',
                    fontSize: 15,
                    lineHeight: 1.3,
                    flexShrink: 0,
                    fontWeight: 700,
                  }}
                >
                  •
                </span>
                <span style={{ flex: 1 }}>{n}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
